/**
 * Regole pure della lista chat (niente React ne' Supabase: i test girano in node).
 *
 * - riconoscere se il testo cercato e' un numero di telefono o un nome;
 * - fissate in cima, archiviate a parte, silenziate;
 * - "Da gestire" conta solo le chat con attivita' negli ultimi 30 giorni.
 */

/** Campi di whatsapp_chat_meta che servono alla lista (tutti facoltativi: righe vecchie o migration non ancora applicata). */
export interface ChatListPrefs {
  is_pinned?: boolean | null;
  is_archived?: boolean | null;
  archived_at?: string | null;
  muted_until?: string | null;
}

export interface ChatLike {
  id: string;
  lastMessage?: { timestamp: number; fromMe: boolean } | null;
}

/** Oltre questa data un "silenzia sempre" e' di fatto per sempre. */
export const MUTE_FOREVER_ISO = '2099-12-31T23:59:59.000Z';
export const DA_GESTIRE_GIORNI = 30;

/**
 * True se il testo sembra un numero di telefono: solo cifre dopo aver tolto spazi,
 * trattini, punti, parentesi e il "+" iniziale, fra 6 e 15 cifre (limite E.164).
 */
export function looksLikePhoneNumber(text: string | null | undefined): boolean {
  const t = (text || '').trim();
  if (!t) return false;
  if (!/^\+?[\d\s\-().\/]+$/.test(t)) return false;
  const digits = t.replace(/\D/g, '');
  return digits.length >= 6 && digits.length <= 15;
}

/** Numero leggibile per la riga "Scrivi a ...": +39 333 123 4567 (solo raggruppamento, nessuna validazione). */
export function formatPhoneForDisplay(digits: string): string {
  if (!digits) return '';
  if (digits.startsWith('39') && digits.length >= 11) {
    const rest = digits.slice(2);
    return `+39 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`.trim();
  }
  return `+${digits}`;
}

/**
 * Chat di sistema di WhatsApp da non mostrare in elenco (28/09/2026): gli aggiornamenti di
 * stato arrivano come chat "status@broadcast" e comparivano come una conversazione "status".
 * Anche i canali (@newsletter) non sono conversazioni con un cliente.
 */
export function isHiddenSystemChat(chatId: string | null | undefined): boolean {
  if (!chatId) return false;
  return chatId === 'status@broadcast' || chatId.endsWith('@broadcast') || chatId.endsWith('@newsletter');
}

/**
 * WhatsApp manda il nome dei numeri non in rubrica gia' nascosto ("+39∙∙∙∙∙∙∙∙28"),
 * anche se l'identificativo della chat contiene il numero intero (28/09/2026).
 */
export function isMaskedPhoneName(name: string | null | undefined): boolean {
  return !!name && /[∙•·]{3,}/.test(name) && /^[+\d\s∙•·]+$/.test(name.trim());
}

/**
 * Nome da mostrare: se quello di WhatsApp e' vuoto o nascosto coi pallini si usa il nome
 * conosciuto del contatto, altrimenti il numero intero preso dall'identificativo (@c.us).
 */
export function chatDisplayName(
  rawName: string | null | undefined,
  chatId: string,
  knownName?: string | null,
): string {
  const name = (rawName || '').trim();
  if (name && !isMaskedPhoneName(name)) return name;
  if (knownName && knownName.trim() && !isMaskedPhoneName(knownName)) return knownName.trim();
  const [user, server] = chatId.split('@');
  if (server === 'c.us' && /^\d{6,15}$/.test(user)) return formatPhoneForDisplay(user);
  return name || user || 'Sconosciuto';
}

/**
 * Timestamp dell'ultimo messaggio in millisecondi. WAHA lo da' in secondi, la lista
 * Cloud (dal DB) in millisecondi: sotto 10^12 e' per forza in secondi.
 */
export function lastActivityMs(chat: ChatLike): number {
  const ts = chat.lastMessage?.timestamp || 0;
  if (!ts) return 0;
  return ts < 1e12 ? ts * 1000 : ts;
}

export function isMuted(prefs: ChatListPrefs | null | undefined, now: number = Date.now()): boolean {
  if (!prefs?.muted_until) return false;
  const until = new Date(prefs.muted_until).getTime();
  return Number.isFinite(until) && until > now;
}

export function isPinned(prefs: ChatListPrefs | null | undefined): boolean {
  return !!prefs?.is_pinned;
}

/**
 * Archiviata e ancora da nascondere: se dopo l'archiviazione e' arrivato un messaggio
 * dal cliente la chat torna nella lista (come fa WhatsApp), senza scrivere nulla nel DB.
 */
export function isArchived(prefs: ChatListPrefs | null | undefined, chat: ChatLike): boolean {
  if (!prefs?.is_archived) return false;
  const archivedAt = prefs.archived_at ? new Date(prefs.archived_at).getTime() : 0;
  if (!archivedAt || !chat.lastMessage || chat.lastMessage.fromMe) return true;
  return lastActivityMs(chat) <= archivedAt;
}

/**
 * Divide la lista in visibili e archiviate e porta le fissate in cima. L'ordine di
 * partenza (ultima attivita') resta invariato dentro ciascun gruppo.
 */
export function arrangeChats<T extends ChatLike>(
  chats: T[],
  prefsOf: (id: string) => ChatListPrefs | null | undefined,
): { visible: T[]; archived: T[] } {
  const pinned: T[] = [];
  const rest: T[] = [];
  const archived: T[] = [];
  for (const chat of chats) {
    const prefs = prefsOf(chat.id);
    if (isArchived(prefs, chat)) archived.push(chat);
    else if (isPinned(prefs)) pinned.push(chat);
    else rest.push(chat);
  }
  return { visible: [...pinned, ...rest], archived };
}

/** Attivita' negli ultimi `giorni` giorni: il filtro "Da gestire" ignora le chat ferme da mesi. */
export function hasRecentActivity(chat: ChatLike, now: number = Date.now(), giorni: number = DA_GESTIRE_GIORNI): boolean {
  const ts = lastActivityMs(chat);
  if (!ts) return false;
  return now - ts <= giorni * 24 * 60 * 60 * 1000;
}

export type MuteOption = '8h' | '1w' | 'always';

export function muteUntilFor(option: MuteOption, now: number = Date.now()): string {
  if (option === '8h') return new Date(now + 8 * 60 * 60 * 1000).toISOString();
  if (option === '1w') return new Date(now + 7 * 24 * 60 * 60 * 1000).toISOString();
  return MUTE_FOREVER_ISO;
}

/**
 * Totale dei non letti per il badge globale, senza le chat silenziate (muted_until
 * nel futuro o "sempre"), come fa WhatsApp. Le chat senza preferenze caricate contano.
 */
export function unreadTotalExcludingMuted(
  chats: Array<{ id: string; unreadCount?: number | null }>,
  prefsOf: (id: string) => ChatListPrefs | null | undefined,
  now: number = Date.now(),
): number {
  let total = 0;
  for (const chat of chats) {
    const n = chat.unreadCount || 0;
    if (n > 0 && !isMuted(prefsOf(chat.id), now)) total += n;
  }
  return total;
}

const PREVIEW_BY_TYPE: Record<string, string> = {
  image: 'Foto',
  video: 'Video',
  audio: 'Audio',
  ptt: 'Messaggio vocale',
  voice: 'Messaggio vocale',
  document: 'Documento',
  sticker: 'Adesivo',
  location: 'Posizione',
  vcard: 'Contatto',
  contact: 'Contatto',
  contacts: 'Contatto',
  multi_vcard: 'Contatti',
  poll: 'Sondaggio',
  poll_creation: 'Sondaggio',
  call_log: 'Chiamata',
  revoked: 'Messaggio eliminato',
  ciphertext: 'Messaggio in attesa',
};

/**
 * Testo dell'ultimo messaggio nella lista chat. "Nessun messaggio" solo se la chat non ne ha;
 * se l'ultimo e' una foto, un vocale o un messaggio di sistema senza testo si scrive il tipo
 * (prima i gruppi mostravano "Nessun messaggio" pur avendo decine di non letti, 27/09/2026).
 */
export function lastMessagePreviewText(
  lastMessage: { body?: string | null; type?: string | null } | null | undefined,
): string {
  if (!lastMessage) return 'Nessun messaggio';
  const body = (lastMessage.body ?? '').trim();
  if (body) return body.replace(/@(\d{5,})/g, '@utente');
  return PREVIEW_BY_TYPE[(lastMessage.type ?? '').toLowerCase()] ?? 'Messaggio';
}

const NOWEB_KEYS: Array<[string, string]> = [
  ['imageMessage', 'image'],
  ['videoMessage', 'video'],
  ['documentMessage', 'document'],
  ['documentWithCaptionMessage', 'document'],
  ['stickerMessage', 'sticker'],
  ['locationMessage', 'location'],
  ['liveLocationMessage', 'location'],
  ['contactMessage', 'contact'],
  ['contactsArrayMessage', 'contact'],
  ['pollCreationMessage', 'poll'],
  ['pollCreationMessageV3', 'poll'],
];

/**
 * Tipo dell'ultimo messaggio di /chats/overview. Con il motore NOWEB WAHA non manda `type`:
 * lo si ricava dal tipo del file o dalla chiave del messaggio originale (27/09/2026).
 */
export function inferWahaMessageType(msg: {
  type?: string | null;
  hasMedia?: boolean;
  media?: { mimetype?: string | null } | null;
  _data?: { message?: Record<string, unknown> | null } | null;
} | null | undefined): string {
  if (!msg) return 'text';
  if (msg.type && msg.type !== 'chat') return msg.type;
  const message = msg._data?.message ?? null;
  if (message && typeof message === 'object') {
    const audio = (message as Record<string, { ptt?: boolean } | undefined>).audioMessage;
    if (audio) return audio.ptt ? 'ptt' : 'audio';
    for (const [key, type] of NOWEB_KEYS) if (key in message) return type;
  }
  const mime = (msg.media?.mimetype ?? '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime) return 'document';
  if (msg.hasMedia) return 'document';
  return msg.type === 'chat' ? 'text' : 'text';
}
